import { ApolloError, FetchResult, gql, useMutation } from '@apollo/client';
import { CAPTCHA_TOKEN_HEADER, TurnstileWidgetProps, useCaptchaChallenge } from '@kompare/captcha-react';
import { useRef, useState } from 'react';
import {
  CaptchaPage,
  describeInput,
  newChallengeId,
  reportCaptchaRequired,
  reportSubmitOutcome,
  reportTurnstileEvent,
} from 'src/common/utils/captchaTelemetry.util';
import { i18n } from 'src/common/utils/translate.util';
import config from 'src/config';
import { captchaStatusOf, submitStatusOf } from '../captcha';
import { CreateSoInput } from '../types/types';

export const CREATE_SO_MUTATION = gql(`
  mutation CreateSo($input: CreateSoInput!) {
      createSo(input: $input)
  }
`);

export type CreateSoData = { createSo: string };
type CreateSoVars = { input: CreateSoInput };

export interface SubmitHandlers {
  onSuccess: (result: FetchResult<CreateSoData>) => void;
  /** Every failure except CAPTCHA_REQUIRED — the hook owns that branch. */
  onError?: (error: ApolloError) => void;
  /** Runs after EVERY attempt, including one that ended in a challenge (drop loaders here). */
  onSettled?: () => void;
}

export interface CaptchaState {
  /** Render the widget when true (and keep the submit buttons disabled — the widget resubmits). */
  required: boolean;
  /** Copy to show under the widget while a challenge is due; null otherwise. */
  message: string | null;
  widgetProps: Pick<TurnstileWidgetProps, 'siteKey' | 'onToken' | 'onEvent' | 'resetSignal'>;
}

interface Options {
  page: CaptchaPage;
}

/**
 * `createSo` behind travel-insurance-api's captcha ladder (`@kompare/rate-limit` README,
 * "Conditional captcha"; the GraphQL flavour from the funnel-hardening playbook §5.2,
 * copied from dzo-webshop's `useCreateSoWithCaptcha`).
 *
 * Submit normally. On `CAPTCHA_REQUIRED` (HTTP 200, `extensions.statusCode` 428) the hook
 * arms the Turnstile widget, keeps the denied `variables` object and resends that very
 * same object with the token in the `x-captcha-token` header; the caller's handlers see
 * the retry's outcome as if it were the first attempt. Why the same object: the API counts
 * attempts, and a payload re-derived from form state between denial and retry could
 * differ, making the retry a different request than the one the user was challenged for.
 *
 * A 428 on the retry (spent/expired/rejected token, siteverify outage) resets the widget
 * for another solve. Any other failure of the retry (RATE_LIMITED / IP_BLOCKED,
 * validation, network) is terminal for that payload: the pending retry is dropped, the
 * widget is dismissed and the buttons re-enabled, so the next attempt is an explicit one.
 * Rate-limit denials reach `onError`; the global `rateLimitErrorLink` already toasts them.
 */
export function useCreateSoWithCaptcha({ page }: Options) {
  // errorPolicy 'none' is required: under 'all' a CAPTCHA_REQUIRED response resolves
  // instead of throwing and the challenge branch below never runs.
  const [mutate, { loading }] = useMutation<CreateSoData, CreateSoVars>(CREATE_SO_MUTATION, {
    errorPolicy: 'none',
  });
  const challenge = useCaptchaChallenge();
  const pendingRef = useRef<{ variables: CreateSoVars; handlers: SubmitHandlers } | null>(null);
  const inFlightRef = useRef(false);
  const [message, setMessage] = useState<string | null>(null);
  // `useCaptchaChallenge` stays `required` once armed; this hides the widget after a
  // terminal error so a fresh token does not keep replaying a failing request.
  const [dismissed, setDismissed] = useState(false);
  // Telemetry correlation id shared by every event of the challenge in progress.
  const challengeIdRef = useRef<string | null>(null);

  const run = async (variables: CreateSoVars, handlers: SubmitHandlers, token?: string) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const input = describeInput(variables.input?.email);
    try {
      const result = await mutate({
        variables,
        context: token ? { headers: { [CAPTCHA_TOKEN_HEADER]: token } } : undefined,
      });
      reportSubmitOutcome(page, 200, input, !!token, challengeIdRef.current ?? undefined);
      challengeIdRef.current = null;
      pendingRef.current = null;
      setMessage(null);
      handlers.onSuccess(result);
    } catch (e) {
      const error = e as ApolloError;
      const challenged = challenge.handleStatus(captchaStatusOf(error));
      if (challenged && !challengeIdRef.current) challengeIdRef.current = newChallengeId();
      reportSubmitOutcome(page, submitStatusOf(error), input, !!token, challengeIdRef.current ?? undefined);
      if (challenged) {
        // Keep the exact payload for the token retry; `handleStatus` resets the widget on a repeat.
        pendingRef.current = { variables, handlers };
        setDismissed(false);
        reportCaptchaRequired(page, challengeIdRef.current as string, input);
        setMessage(i18n.t('common.messages.captchaRequired'));
        return;
      }
      // Terminal failure: drop the pending retry.
      challengeIdRef.current = null;
      pendingRef.current = null;
      setMessage(null);
      if (token) setDismissed(true);
      handlers.onError?.(error);
    } finally {
      inFlightRef.current = false;
      handlers.onSettled?.();
    }
  };

  /** `run` never rejects (everything is routed to the handlers), so the promise is dropped. */
  const submit = (input: CreateSoInput, handlers: SubmitHandlers) => {
    run({ input }, handlers);
  };

  const onToken = (token: string) => {
    const pending = pendingRef.current;
    if (pending) run(pending.variables, pending.handlers, token);
  };

  const captcha: CaptchaState = {
    required: challenge.required && !dismissed,
    message,
    widgetProps: {
      siteKey: config.REACT_APP_TURNSTILE_SITE_KEY,
      onToken,
      onEvent: (event) => reportTurnstileEvent(page, event, challengeIdRef.current ?? undefined),
      resetSignal: challenge.resetSignal,
    },
  };

  return { submit, loading, captcha };
}
